import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineCube,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark,
  HiOutlineArrowPath
} from 'react-icons/hi2';
import { authService, productService } from '../../services';
import { usePermissions } from '../../hooks/usePermissions';
import SharedModal from '../../components/common/SharedModal';
import { toast } from 'react-toastify';
import PageLoading from '../../components/common/PageLoading';
import './Products.css';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false); // Start with false to show page immediately
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All'); // Filter: All, Active, Inactive
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    productCode: '',
    productName: '',
    productGroup: '',
    uom: '',
    costPrice: '',
    status: 'Active',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();
  const { hasPermission } = usePermissions();

  // Permission checks
  const canView = hasPermission('Product.View');
  const canCreate = hasPermission('Product.Create');
  const canEdit = hasPermission('Product.Edit');
  const canDelete = hasPermission('Product.Delete');

  useEffect(() => {
    if (tenantId && canView) {
      // Load data in background without blocking UI
      // Use setTimeout to ensure page renders first
      const timer = setTimeout(() => {
        loadProducts(false);
      }, 0);
      return () => clearTimeout(timer);
    } else if (!canView) {
      setError('You do not have permission to view products');
    } else {
      setError('Please login to view products');
    }
  }, [tenantId, canView]);

  const loadProducts = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError('');
      const data = await productService.getAllProducts();
      setProducts(data || []);
    } catch (err) {
      setError('Failed to load products. Please try again.');
      console.error('Error loading products:', err);
      setProducts([]);
    } finally {
      setLoading(false); // Always set loading to false after API call completes
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredProducts = products.filter((product) => {
    // Search filter
    const matchesSearch =
      product.productName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.productCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.productGroup?.toLowerCase().includes(searchTerm.toLowerCase());

    // Status filter
    const matchesStatus =
      statusFilter === 'All' ||
      (statusFilter === 'Active' && product.status === 'Active') ||
      (statusFilter === 'Inactive' && product.status === 'Inactive');

    return matchesSearch && matchesStatus;
  });

  const handleAdd = () => {
    if (!canCreate) {
      setError('You do not have permission to create products');
      return;
    }
    setEditingProduct(null);
    setFormData({
      productCode: '',
      productName: '',
      productGroup: '',
      uom: '',
      costPrice: '',
      status: 'Active',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (product) => {
    if (!canEdit) {
      setError('You do not have permission to edit products');
      return;
    }
    setEditingProduct(product);
    setFormData({
      productCode: product.productCode || '',
      productName: product.productName || '',
      productGroup: product.productGroup || '',
      uom: product.uom || '',
      costPrice: product.costPrice || '',
      status: product.status || 'Active',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (product) => {
    try {
      setError('');
      // Mark product as inactive instead of deleting
      const productData = {
        productId: product.productId,
        productCode: product.productCode,
        productName: product.productName,
        productGroup: product.productGroup || null,
        uom: product.uom || null,
        status: 'Inactive', // Mark as inactive
        cycleTimes: [], // Navigation property
        productSkus: [] // Navigation property
      };
      await productService.updateProduct(product.productId, productData);
      await loadProducts(false); // Don't show loading on refresh after delete
      setDeleteConfirm(null);
      toast.success("Product deactivated successfully!");
    } catch (err) {
      const msg = "Failed to delete product. Please try again.";
      setError(msg);
      toast.error(msg);
      console.error('Error deleting product:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.productName.trim()) {
      errors.productName = 'Product name is required';
    } else if (formData.productName.length > 150) {
      errors.productName = 'Product name must be less than 150 characters';
    }

    if (formData.productCode && formData.productCode.length > 50) {
      errors.productCode = 'Product code must be less than 50 characters';
    }

    if (formData.productGroup && formData.productGroup.length > 100) {
      errors.productGroup = 'Product group must be less than 100 characters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check permissions before submitting
    if (editingProduct && !canEdit) {
      setError('You do not have permission to edit products');
      return;
    }
    if (!editingProduct && !canCreate) {
      setError('You do not have permission to create products');
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const productData = {
        productCode: formData.productCode.trim() || null,
        productName: formData.productName.trim(),
        productGroup: formData.productGroup.trim() || null,
        uom: formData.uom.trim() || null,
        costPrice: formData.costPrice ? parseFloat(formData.costPrice) : null,
        status: formData.status,
        cycleTimes: [], // Navigation property
        productSkus: [] // Navigation property
      };

      if (editingProduct) {
        productData.productId = editingProduct.productId;
        await productService.updateProduct(editingProduct.productId, productData);
      } else {
        await productService.createProduct(productData);
      }

      await loadProducts(false); // Don't show loading on refresh after save
      setShowModal(false);
      setFormData({ productCode: '', productName: '', productGroup: '', uom: '', status: 'Active' });
      setEditingProduct(null);
      toast.success(editingProduct ? "Product updated successfully!" : "Product created successfully!");
    } catch (err) {
      let errorMessage = 'Failed to save product. Please try again.';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.errors) {
        const errors = err.response.data.errors;
        errorMessage = Object.values(errors).flat().join(', ');
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProduct(null);
    setFormData({ productCode: '', productName: '', productGroup: '', uom: '', status: 'Active' });
    setFormErrors({});
  };

  return (
    <div className="pagecontainer">
      <div className="page-header">
        <div className="header-content">
          <h1>Products</h1>
          <p>Manage your organization products</p>
        </div>
        <div className='header-actions'>
          {canCreate && (
            <button className="btn-primary btn" onClick={handleAdd}>
              <HiOutlinePlus />
              Add Product
            </button>

          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container pad10 kizantable">
        <div className="table-header">
          <div className='left-group'>
            <div className="search-box">
              <HiOutlineMagnifyingGlass className="search-icon" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={handleSearch}
                className="search-input"
              />
            </div>
            <div style={{ display: "flex", flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
              <div className="filter-group">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="status-filter"
                >
                  <option value="All">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
              <div className="table-info">
                {loading && products.length === 0 ? (
                  <PageLoading variant="muted" message="Loading…" />
                ) : (
                  `${filteredProducts.length} ${filteredProducts.length === 1 ? 'product' : 'products'}`
                )}
              </div>
            </div>
          </div>
        </div>

        {loading && products.length === 0 ? (
          <div className="table-loading">
            <PageLoading variant="compact" message="Loading products…" />
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="empty-state">
            <HiOutlineCube className="empty-icon" />
            <h3>No products found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first product'}
            </p>
            {!searchTerm && canCreate && (
              <button className="btn-primary" onClick={handleAdd}>
                New Product
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Code</th>
                <th>Product Name</th>
                <th>Group</th>
                <th>UOM</th>
                <th>Cost / Price</th>
                <th>Status</th>
                <th>Created At</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => (
                <tr key={product.productId} className={product.status === 'Inactive' ? 'inactive-row' : ''}>
                  <td>{product.productId}</td>
                  <td>{product.productCode || '-'}</td>
                  <td className="name-cell">{product.productName}</td>
                  <td>{product.productGroup || '-'}</td>
                  <td>{product.uom || '-'}</td>
                  <td>{product.costPrice !== null && product.costPrice !== undefined ? product.costPrice : '-'}</td>
                  <td>
                    <span className={`status-badge ${product.status?.toLowerCase()}`}>
                      {product.status || 'Active'}
                    </span>
                  </td>
                  <td>
                    {product.createdAt
                      ? new Date(product.createdAt).toLocaleDateString()
                      : '-'}
                  </td>
                  <td>
                    <div className="action-buttons">
                      {canEdit && (
                        <button
                          className="btn-icon-edit"
                          onClick={() => handleEdit(product)}
                          title="Edit"
                        >
                          <HiOutlinePencil />
                        </button>
                      )}
                      {canDelete && product.status === 'Active' && (
                        <button
                          className="btn-icon-delete"
                          onClick={() => setDeleteConfirm(product)}
                          title="Delete"
                        >
                          <HiOutlineTrash />
                        </button>
                      )}
                      {canEdit && product.status === 'Inactive' && (
                        <button
                          className="btn-icon-restore"
                          onClick={async () => {
                            try {
                              const productData = {
                                productId: product.productId,
                                productCode: product.productCode,
                                productName: product.productName,
                                productGroup: product.productGroup || null,
                                uom: product.uom || null,
                                status: 'Active',
                                cycleTimes: [],
                                productSkus: []
                              };
                              await productService.updateProduct(product.productId, productData);
                              await loadProducts(false);
                              toast.success("Product restored successfully!");
                            } catch (err) {
                              const msg = 'Failed to restore product. Please try again.';
                              setError(msg);
                              toast.error(msg);
                              console.error('Error restoring product:', err);
                            }
                          }}
                          title="Restore"
                        >
                          <HiOutlineArrowPath />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Modal */}
      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingProduct ? 'Edit Product' : 'New Product'}
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="product-form"
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting
                ? editingProduct
                  ? 'Updating...'
                  : 'Creating...'
                : editingProduct
                  ? 'Update'
                  : 'Create'}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="product-form" onSubmit={handleSubmit}>
            <div className='row'>
              <div className="form-group">
                <label htmlFor="productCode">Product Code</label>
                <input
                  type="text"
                  id="productCode"
                  value={formData.productCode}
                  onChange={(e) =>
                    setFormData({ ...formData, productCode: e.target.value })
                  }
                  className={formErrors.productCode ? 'error' : ''}
                  placeholder="Enter product code"
                  maxLength={50}
                />
                {formErrors.productCode && (
                  <span className="field-error">{formErrors.productCode}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="productName">
                  Product Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="productName"
                  value={formData.productName}
                  onChange={(e) =>
                    setFormData({ ...formData, productName: e.target.value })
                  }
                  className={formErrors.productName ? 'error' : ''}
                  placeholder="Enter product name"
                  maxLength={150}
                />
                {formErrors.productName && (
                  <span className="field-error">{formErrors.productName}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="productGroup">Product Group</label>
                <input
                  type="text"
                  id="productGroup"
                  value={formData.productGroup}
                  onChange={(e) =>
                    setFormData({ ...formData, productGroup: e.target.value })
                  }
                  placeholder="Enter product group"
                  maxLength={100}
                  className={formErrors.productGroup ? 'error' : ''}
                />
                {formErrors.productGroup && (
                  <span className="field-error">{formErrors.productGroup}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="uom">Unit of Measure (UOM)</label>
                <input
                  type="text"
                  id="uom"
                  value={formData.uom}
                  onChange={(e) =>
                    setFormData({ ...formData, uom: e.target.value })
                  }
                  placeholder="e.g., kg, pcs, liters"
                  maxLength={20}
                />
              </div>

              <div className="form-group">
                <label htmlFor="costPrice">Cost / Price</label>
                <input
                  type="number"
                  step="0.01"
                  id="costPrice"
                  value={formData.costPrice}
                  onChange={(e) =>
                    setFormData({ ...formData, costPrice: e.target.value })
                  }
                  placeholder="0.00"
                />
              </div>

              <div className="form-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>
          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customWidth="400px"
        customClass="small"
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => handleDelete(deleteConfirm)}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete{' '}
            <strong>{deleteConfirm?.productName}</strong>?
          </p>
          <p className="warning-text">
            This action cannot be undone. The product will be marked as inactive.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default Products;

